import { Link, useNavigate, useParams } from 'react-router-dom';
import { LifeBuoy, ArrowLeft } from 'lucide-react';
import { Container, PageHeader, SplitView, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { useTicket } from '@/api/support';
import { TicketThread } from './components/TicketThread';
import { TicketMetaRail } from './components/TicketMetaRail';
import { TicketFacts } from './components/TicketFacts';
import { TicketMarkdown } from './components/TicketMarkdown';
import {
  TICKET_STATUS_TONE,
  TICKET_STATUS_LABEL,
  TICKET_PRIORITY_TONE,
  TICKET_PRIORITY_LABEL,
  ticketTypeLabelFromKey,
  formatDate,
} from './shared';

export function TicketDetailPage() {
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const id = Number(ticketId);

  const { data: ticket, isLoading } = useTicket(Number.isNaN(id) ? undefined : id);

  const backLink = (
    <Breadcrumbs>
      <BreadcrumbItem asChild>
        <Link to="/support">Support</Link>
      </BreadcrumbItem>
      <BreadcrumbItem current>{`#${id}`}</BreadcrumbItem>
    </Breadcrumbs>
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <PageHeader breadcrumbs={backLink} title={`Ticket #${id}`} icon={<LifeBuoy size={22} />} />
        <SplitView aside={<Skeleton style={{ height: 220 }} />} asideWidth="320px">
          <Skeleton style={{ height: 360 }} />
        </SplitView>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container size="xl">
        <PageHeader breadcrumbs={backLink} title="Ticket not found" icon={<LifeBuoy size={22} />} />
        <EmptyState
          icon={<LifeBuoy size={28} />}
          title="Ticket not found"
          description="This ticket doesn't exist or you don't have access to it."
          actions={
            <Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={() => navigate('/support')}>
              Back to Support
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={backLink}
        icon={<LifeBuoy size={22} />}
        title={`#${ticket.ticket_id} · ${ticketTypeLabelFromKey(ticket.ticket_type_key)}`}
        description={[
          ticket.service_name || ticket.ticket_product_name,
          `Opened ${formatDate(ticket.ticket_requested_at || ticket.created_at)}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Badge tone={TICKET_PRIORITY_TONE[ticket.ticket_priority]} appearance="outline" size="sm" dot>
              {TICKET_PRIORITY_LABEL[ticket.ticket_priority]}
            </Badge>
            <StatusPill
              tone={TICKET_STATUS_TONE[ticket.ticket_status]}
              loading={ticket.ticket_status === 'work-in-progress'}
            >
              {TICKET_STATUS_LABEL[ticket.ticket_status]}
            </StatusPill>
          </div>
        }
      />

      <SplitView aside={<TicketMetaRail ticket={ticket} />} asideWidth="320px">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <TicketFacts ticket={ticket} />

          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardBody>
              {ticket.ticket_content ? (
                <TicketMarkdown source={ticket.ticket_content} />
              ) : (
                <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  (no description provided)
                </p>
              )}
            </CardBody>
          </Card>

          <TicketThread
            ticketId={ticket.ticket_id}
            status={ticket.ticket_status}
            comments={ticket.comments}
          />
        </div>
      </SplitView>
    </Container>
  );
}
